import React, { useEffect, useRef } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import useStore from "./store/useStore";
import api from "./services/api";
import RouteMeta from "./components/common/RouteMeta";

// Website Pages
import WebLayout from "./website/components/WebLayout";
import Home from "./website/pages/Home";
import Features from "./website/pages/Features";
import HowItWorks from "./website/pages/HowItWorks";
import Pricing from "./website/pages/Pricing";
import Contact from "./website/pages/Contact";

// Dashboard Pages
import Layout from "./components/layout/Layout";
import Dashboard from "./pages/Dashboard";
import Assets from "./pages/Assets";
import AssetOwners from "./pages/AssetOwners";
import Tenants from "./pages/Tenants";
import Rentals from "./pages/Rentals";
import Payments from "./pages/Payments";
import Maintenance from "./pages/Maintenance";
import Users from "./pages/Users";
import CompanyUsers from "./pages/CompanyUsers";
import Profile from "./pages/Profile";
import CompanySettings from "./pages/CompanySettings";
import Report from "./pages/Report";
import SuperAdminReports from "./pages/SuperAdminReports";
import Invoice from "./pages/Invoice";
import ImportRecords from "./pages/ImportRecords";
import Notifications from "./pages/Notifications";
import Messages from "./pages/Messages";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import SuperAdminDashboard from "./pages/SuperAdminDashboard";
import SuperAdminCompanies from "./pages/SuperAdminCompanies";
import SuperAdminSubscriptions from "./pages/SuperAdminSubscriptions";
import SuperAdminSystemSettings from "./pages/SuperAdminSystemSettings";

const ADMIN_ROLES = ["SuperAdmin", "CompanyAdmin"];
const IMPORT_ROLES = ["SuperAdmin", "CompanyAdmin", "Staff"];
const OPERATIONAL_ROLES = ["SuperAdmin", "CompanyAdmin", "Staff", "Viewer"];

const StoreToastStack = () => {
  const { toasts, removeToast } = useStore();

  const toneClass = {
    success: "border-emerald-200 bg-emerald-50 text-emerald-900",
    error: "border-rose-200 bg-rose-50 text-rose-900",
    warning: "border-amber-200 bg-amber-50 text-amber-900",
    info: "border-slate-200 bg-slate-50 text-slate-900"
  };

  const icon = {
    success: "✓",
    error: "!",
    warning: "⚠",
    info: "i"
  };

  return (
    <div className="fixed top-4 right-4 z-[99999] flex flex-col gap-3 w-[min(380px,92vw)] pointer-events-none">
      {toasts.map((item) => (
        <div
          key={item.id}
          className={`pointer-events-auto border shadow-lg rounded-xl px-4 py-3 text-sm font-medium backdrop-blur-sm animate-[toastIn_220ms_ease] ${toneClass[item.type] || toneClass.info}`}
          role="status"
          aria-live="polite"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <span className="inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-white/80 border border-current/20 text-xs font-bold">
                {icon[item.type] || icon.info}
              </span>
              <span className="leading-5 break-words">{item.message}</span>
            </div>
            <button
              type="button"
              onClick={() => removeToast(item.id)}
              className="opacity-70 hover:opacity-100 transition-opacity"
              aria-label="Dismiss notification"
            >
              ×
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useStore();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
};

const RoleRoute = ({ children, roles }) => {
  const { user, authRole } = useStore();
  const role = authRole || user?.role;
  if (!roles.includes(role)) return <Navigate to="/dashboard" replace />;
  return children;
};

const SuperAdminRoute = ({ children }) => {
  const { user, authRole } = useStore();
  const role = authRole || user?.role;
  if (role !== "SuperAdmin") return <Navigate to="/dashboard" replace />;
  return children;
};

const DashboardEntry = () => {
  const { user, authRole } = useStore();
  const role = authRole || user?.role;
  if (role === "SuperAdmin") return <Navigate to="/superadmin" replace />;
  return (
    <Layout>
      <Dashboard />
    </Layout>
  );
};

const ReportsEntry = () => {
  const { user, authRole } = useStore();
  const role = authRole || user?.role;
  if (role === "SuperAdmin") return <SuperAdminReports />;
  return <Report />;
};

const AppRoutes = () => {
  const authSyncedRef = useRef(false);
  const { isAuthenticated, setAuthContext } = useStore();

  useEffect(() => {
    if (!isAuthenticated) {
      authSyncedRef.current = false;
      return;
    }

    if (authSyncedRef.current) return;

    authSyncedRef.current = true;

    const syncAuthContext = async () => {
      try {
        const res = await api.get('/auth/me');
        if (res.data?.success && res.data?.data) {
          setAuthContext(res.data.data);
        }
      } catch (error) {
        // A 401 here usually means there is no valid session anymore.
        if (error.response?.status !== 401) {
          console.error('Auth context sync failed:', error);
        }
      }
    };

    syncAuthContext();
  }, [isAuthenticated, setAuthContext]);

  return (
    <>
      <RouteMeta />
      <Routes>
        {/* Public Website Routes */}
        <Route element={<WebLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/features" element={<Features />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/contact" element={<Contact />} />
        </Route>

        {/* Auth Routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Protected Dashboard Routes */}
        <Route path="/dashboard" element={<ProtectedRoute><DashboardEntry /></ProtectedRoute>} />
        <Route path="/superadmin" element={<ProtectedRoute><SuperAdminRoute><Layout><SuperAdminDashboard /></Layout></SuperAdminRoute></ProtectedRoute>} />
        <Route path="/superadmin/companies" element={<ProtectedRoute><SuperAdminRoute><Layout><SuperAdminCompanies /></Layout></SuperAdminRoute></ProtectedRoute>} />
        <Route path="/superadmin/subscriptions" element={<ProtectedRoute><SuperAdminRoute><Layout><SuperAdminSubscriptions /></Layout></SuperAdminRoute></ProtectedRoute>} />
        <Route path="/superadmin/settings" element={<ProtectedRoute><SuperAdminRoute><Layout><SuperAdminSystemSettings /></Layout></SuperAdminRoute></ProtectedRoute>} />
        <Route path="/assets" element={<ProtectedRoute><RoleRoute roles={OPERATIONAL_ROLES}><Layout><Assets /></Layout></RoleRoute></ProtectedRoute>} />
        <Route path="/asset-owners" element={<ProtectedRoute><RoleRoute roles={OPERATIONAL_ROLES}><Layout><AssetOwners /></Layout></RoleRoute></ProtectedRoute>} />
        <Route path="/tenants" element={<ProtectedRoute><RoleRoute roles={OPERATIONAL_ROLES}><Layout><Tenants /></Layout></RoleRoute></ProtectedRoute>} />
        <Route path="/rentals" element={<ProtectedRoute><RoleRoute roles={OPERATIONAL_ROLES}><Layout><Rentals /></Layout></RoleRoute></ProtectedRoute>} />
        <Route path="/payments" element={<ProtectedRoute><RoleRoute roles={OPERATIONAL_ROLES}><Layout><Payments /></Layout></RoleRoute></ProtectedRoute>} />
        <Route path="/maintenance" element={<ProtectedRoute><RoleRoute roles={OPERATIONAL_ROLES}><Layout><Maintenance /></Layout></RoleRoute></ProtectedRoute>} />
        <Route path="/users" element={<ProtectedRoute><RoleRoute roles={ADMIN_ROLES}><Layout><Users /></Layout></RoleRoute></ProtectedRoute>} />
        <Route path="/superadmin/company-users" element={<ProtectedRoute><SuperAdminRoute><Layout><CompanyUsers /></Layout></SuperAdminRoute></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><Layout><Profile /></Layout></ProtectedRoute>} />
        <Route path="/company-settings" element={<ProtectedRoute><RoleRoute roles={ADMIN_ROLES}><Layout><CompanySettings /></Layout></RoleRoute></ProtectedRoute>} />
        <Route path="/imports" element={<ProtectedRoute><RoleRoute roles={IMPORT_ROLES}><Layout><ImportRecords /></Layout></RoleRoute></ProtectedRoute>} />
        <Route path="/notifications" element={<ProtectedRoute><RoleRoute roles={OPERATIONAL_ROLES}><Layout><Notifications /></Layout></RoleRoute></ProtectedRoute>} />
        <Route path="/messages" element={<ProtectedRoute><RoleRoute roles={OPERATIONAL_ROLES}><Layout><Messages /></Layout></RoleRoute></ProtectedRoute>} />
        <Route path="/reports" element={<ProtectedRoute><RoleRoute roles={ADMIN_ROLES}><Layout><ReportsEntry /></Layout></RoleRoute></ProtectedRoute>} />
        <Route path="/invoice" element={<ProtectedRoute><RoleRoute roles={["CompanyAdmin", "Staff", "Viewer"]}><Layout><Invoice /></Layout></RoleRoute></ProtectedRoute>} />

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
};

function App() {
  return (
    <Router>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      <StoreToastStack />

      <AppRoutes />
    </Router>
  );
}

export default App;